// Shared shape for the hand-written topic page layers:
//   /palworld-wiki/<slug>  — game-knowledge wiki articles (src/lib/wiki-pages.ts)
//   /palworld-tcg/<slug>   — exact-match card-game query pages (src/lib/tcg-pages.ts)
//
// These are NOT guides: guides live under /guides and are long-form multi-article
// packs. A topic page answers one exact search query in one screen, leads with a
// single extractable answer paragraph, and then sends the reader to the live data
// page or calculator that does the work. Every field here is written per page so
// no two URLs share a title, description, answer or body.

export interface TopicFact {
  label: string;
  value: string;
}

export interface TopicSection {
  heading: string;
  body: string;
  bullets?: string[];
}

/**
 * Optional structured data layer. Competitor wikis answer these queries in
 * prose; a sortable/scannable table plus Dataset markup is the differentiator,
 * so pages that own a numeric answer carry one.
 */
export interface TopicTable {
  caption: string;
  columns: string[];
  rows: string[][];
  /** Keywords for the Dataset JSON-LD emitted when a table exists. */
  keywords: string[];
}

export interface TopicLink {
  to: string;
  label: string;
  note: string;
}

export interface TopicPage {
  slug: string;
  /** Rendered <title>. MUST stay <= 60 characters and be globally unique. */
  title: string;
  /** Single H1. Leads with "Palworld" per the site-wide heading rule. */
  h1: string;
  /** Meta description, <= 160 characters. */
  description: string;
  /** The answer-box paragraph. Marked .aeo-answer and reused in JSON-LD. */
  answer: string;
  facts: TopicFact[];
  table?: TopicTable;
  sections: TopicSection[];
  faq: { q: string; a: string }[];
  links: TopicLink[];
}

export function findTopic(pages: TopicPage[], slug: string): TopicPage | undefined {
  const key = slug.toLowerCase();
  return pages.find((p) => p.slug === key);
}
